/* eslint-disable sonarjs/max-switch-cases */
import { UserActions, UserActionTypes } from './user.actions';

import { UserProfile, AuthMethod } from '../user-profile/user-profile.model';
import { ProviderTin, TinMapObject } from '../user-tins/tins.model';
import { ServiceError } from '@cigna/chcp/shared/util';
import { UserAlert } from '../user-alerts/user-alerts.model';

export const USER_FEATURE_KEY = 'user';

export const parseProviderTins = (
  tinsFromResponse: Map<string, TinMapObject>,
): ProviderTin[] => {
  const tins: ProviderTin[] = [];
  const tinMap: Map<string, TinMapObject> = new Map(
    Object.entries(tinsFromResponse),
  );
  tinMap.forEach((value: TinMapObject, key: string) => {
    tins.push({
      tinId: key,
      tinName: value.name,
      tinLob: value.lob,
      contracted: value.contracted === 'Y' ? true : false,
      hasGovrnm: value.hasGovrnm,
      hasComrcl: value.hasComrcl,
      sanctioned: value.sanctioned,
      generatedTinId: value.generatedTinId,
      entitlements: value.entitlements,
      entitlementsJWTFormatted: value.entitlements,
    });
  });
  return tins;
};

export interface UserState {
  profile: UserProfile | null;
  tins: ProviderTin[];
  tinStatus: string;
  securityQuestions: string[];
  isPatientsLoading: boolean;
  isClaimsLoading: boolean;
  hideEmailPopup: boolean;
  updatePrefLobSuccess: boolean;
  alerts: UserAlert[];
  alertsLoading: boolean;
  alertsError: ServiceError | null;
  alertsUpdateLoading: boolean;
  alertsUpdateSuccess: boolean;
  alertsUpdateError: ServiceError | null;
}

export const initialState: UserState = {
  profile: null,
  tins: [],
  tinStatus: 'default',
  securityQuestions: [],
  isPatientsLoading: true,
  isClaimsLoading: true,
  hideEmailPopup: false,
  updatePrefLobSuccess: false,
  alerts: [],
  alertsLoading: false,
  alertsError: null,
  alertsUpdateLoading: false,
  alertsUpdateSuccess: false,
  alertsUpdateError: null,
};

export interface UserPartialState {
  readonly [USER_FEATURE_KEY]: UserState;
}

export function userReducer(
  state = initialState,
  action: UserActions,
): UserState {
  switch (action.type) {
    case UserActionTypes.GetUserProfileSuccess: {
      return {
        ...state,
        profile: {
          ...state.profile,
          ...action.payload,
        },
      };
    }
    case UserActionTypes.GetLoginUserProfile: {
      return {
        ...state,
        tinStatus: 'loading',
      };
    }
    case UserActionTypes.GetLoginUserProfileSuccess: {
      return {
        ...state,
        profile: { ...action.payload, phoneExtn: action.payload.phoneExt },
        tins: parseProviderTins(action.payload.tins),
        tinStatus: 'success',
      };
    }
    case UserActionTypes.GetLoginUserProfileError: {
      return {
        ...state,
        tinStatus: 'error',
      };
    }
    case UserActionTypes.UpdateUserProfile: {
      return {
        ...state,
        updatePrefLobSuccess: false,
      };
    }
    case UserActionTypes.UpdateSkipCountSuccess:
    case UserActionTypes.UpdateUserProfileSuccess: {
      return {
        ...state,
        updatePrefLobSuccess: true,
        profile: {
          ...(state.profile as UserProfile),
          ...action.payload,
        },
      };
    }
    case UserActionTypes.GetSecurityQuestionsSuccess: {
      return {
        ...state,
        securityQuestions: action.payload,
      };
    }

    case UserActionTypes.UpdateSkipCountError:
    case UserActionTypes.HideEmailPopup: {
      return {
        ...state,
        hideEmailPopup: true,
      };
    }
    case UserActionTypes.GetAuthMethodsStatusSuccess: {
      return {
        ...state,
        profile: {
          ...(state.profile as UserProfile),
          auth0EmailVerified:
            action.payload.filter(
              (x: AuthMethod) => x.factor === 'email' && x.isVerified,
            ).length > 0
              ? true
              : false,
          auth0MobileVerified:
            action.payload.filter(
              (x: AuthMethod) => x.factor === 'sms' && x.isVerified,
            ).length > 0
              ? true
              : false,
        },
      };
    }
    case UserActionTypes.UpdateChcpAppAgreed: {
      return {
        ...state,
        profile: {
          ...(state.profile as UserProfile),
          chcpAppAgreed: 'Y',
        },
      };
    }
    case UserActionTypes.UpdateEppAppAgreed: {
      return {
        ...state,
        profile: {
          ...(state.profile as UserProfile),
          eppAppAgreed: 'Y',
        },
      };
    }
    case UserActionTypes.UpdateChcpWamAgreed: {
      return {
        ...state,
        profile: {
          ...(state.profile as UserProfile),
          chcpWamAgreed: 'Y',
        },
      };
    }
    case UserActionTypes.UpdateEppWamAgreed: {
      return {
        ...state,
        profile: {
          ...(state.profile as UserProfile),
          eppWamAgreed: 'Y',
        },
      };
    }
    case UserActionTypes.UpdateNetworkWelcomeIndicator: {
      return {
        ...state,
        profile: {
          ...(state.profile as UserProfile),
          networkWelcomeIndicator: 'N',
        },
      };
    }
    case UserActionTypes.GetUserAlerts: {
      return {
        ...state,
        alertsLoading: true,
        alertsError: null,
      };
    }
    case UserActionTypes.GetUserAlertsSuccess: {
      return {
        ...state,
        alerts: action.payload,
        alertsLoading: false,
        alertsError: null,
      };
    }
    case UserActionTypes.GetUserAlertsError: {
      return {
        ...state,
        alertsLoading: false,
        alertsError: action.payload,
      };
    }
    case UserActionTypes.UpdateUserAlerts: {
      return {
        ...state,
        alertsUpdateLoading: true,
        alertsUpdateSuccess: false,
        alertsUpdateError: null,
      };
    }
    case UserActionTypes.UpdateUserAlertsSuccess: {
      return {
        ...state,
        alerts: state.alerts.filter((alert) => {
          if (alert.status !== 'ACTIVE') {
            return true;
          }

          const update = action.payload[alert.tin];
          return update !== 'dismissed' && update !== 'completed';
        }),
        alertsUpdateLoading: false,
        alertsUpdateSuccess: true,
        alertsUpdateError: null,
      };
    }
    case UserActionTypes.UpdateUserAlertsError: {
      return {
        ...state,
        alertsUpdateLoading: false,
        alertsUpdateSuccess: false,
        alertsUpdateError: action.payload,
      };
    }
    default:
      return state;
  }
}
